import httpClient from '../../../../shared/api/httpClient';

/**
 * Crear un nuevo cierre de liquidación (DRAFT)
 */
export async function createPayrollClosure(data) {
  return httpClient.post('/hr/areas/payroll/closures', data);
}

/**
 * Listar cierres de liquidación
 * @param {Object} params - { status?: 'DRAFT' | 'READY' }
 */
export async function listPayrollClosures(params = {}) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, value);
    }
  });
  const url = `/hr/areas/payroll/closures${queryString.toString() ? `?${queryString.toString()}` : ''}`;
  return httpClient.get(url);
}

function buildQueryString(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });
  return query.toString();
}

export async function listPaymentHistoryEmployees() {
  return httpClient.get(
    '/hr/areas/payroll/reports/payment-history/employees',
  );
}

export async function listPaymentHistoryClosures() {
  return httpClient.get(
    '/hr/areas/payroll/reports/payment-history/closures',
  );
}

export async function getEmployeePaymentHistory(params) {
  const query = buildQueryString(params);
  return httpClient.get(
    `/hr/areas/payroll/reports/payment-history?${query}`,
  );
}

export async function downloadEmployeePaymentHistoryPdf(params) {
  const query = buildQueryString(params);
  return httpClient.get(
    `/hr/areas/payroll/reports/payment-history/pdf?${query}`,
    { responseType: 'blob' },
  );
}

export async function getConsolidatedPaymentHistory(params = {}) {
  const query = buildQueryString(params);
  return httpClient.get(
    `/hr/areas/payroll/reports/payment-history/consolidated${query ? `?${query}` : ''}`,
  );
}

export async function downloadConsolidatedPaymentHistoryPdf(params = {}) {
  const query = buildQueryString(params);
  return httpClient.get(
    `/hr/areas/payroll/reports/payment-history/consolidated/pdf${query ? `?${query}` : ''}`,
    { responseType: 'blob' },
  );
}

/**
 * Obtener detalles de un cierre específico
 */
export async function getPayrollClosure(closureId) {
  return httpClient.get(`/hr/areas/payroll/closures/${closureId}`);
}

/**
 * Listar empleados elegibles para un cierre
 */
export async function listEligibleEmployees(closureId, params = {}) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, value);
    }
  });
  const url = `/hr/areas/payroll/closures/${closureId}/eligible-employees${queryString.toString() ? `?${queryString.toString()}` : ''}`;
  return httpClient.get(url);
}

/**
 * Guardar empleados seleccionados para un cierre.
 * El backend automáticamente avanza el currentStep a 3.
 */
export async function saveSelectedEmployees(closureId, employeeIds = []) {
  return httpClient.post(`/hr/areas/payroll/closures/${closureId}/selected-employees`, {
    employees: employeeIds.map((id) => ({ employeeId: id })),
  });
}

/**
 * Obtener empleados seleccionados para un cierre
 */
export async function getSelectedEmployees(closureId) {
  return httpClient.get(`/hr/areas/payroll/closures/${closureId}/selected-employees`);
}

/**
 * Consolidar detalle de liquidación para todos los empleados seleccionados.
 * Calcula assignedMinutes, workedMinutes, balanceAmount y totalAmount.
 * Idempotente: se puede llamar múltiples veces sin perder ediciones manuales previas.
 * @returns {Promise<{ data: ClosureEmployeeDetailDto[], meta: { totalItems, generatedAt } }>}
 */
export async function consolidateClosureEmployees(closureId) {
  return httpClient.post(`/hr/areas/payroll/closures/${closureId}/employees/consolidate`);
}

/**
 * Listar el detalle de liquidación persistido para todos los empleados del cierre.
 * No recalcula — devuelve los datos tal como están en la DB.
 * @returns {Promise<{ data: ClosureEmployeeDetailDto[], meta: { totalItems, generatedAt } }>}
 */
export async function listClosureEmployees(closureId) {
  return httpClient.get(`/hr/areas/payroll/closures/${closureId}/employees`);
}

/**
 * Actualizar campos editables del detalle de un empleado.
 * @param {number} closureId
 * @param {number} employeeId
 * @param {{ assignedMinutes?, workedMinutes?, balanceAmount?, totalAmount?, observations?, employeeReceiptFileId? }} data
 * @returns {Promise<ClosureEmployeeDetailDto>}
 */
export async function updateClosureEmployee(closureId, employeeId, data) {
  return httpClient.patch(
    `/hr/areas/payroll/closures/${closureId}/employees/${employeeId}`,
    data,
  );
}

/**
 * Actualiza el perfil de liquidación de un empleado (Modo de contrato y monto).
 * @param {number} employeeId
 * @param {{ salaryMode: string, baseSalary?: string, hourlyRate?: string }} data
 */
export async function updateEmployeePayrollProfile(employeeId, data) {
  return httpClient.patch(`/hr/areas/payroll/employee-profiles/employee/${employeeId}`, data);
}

/**
 * Actualizar propiedades generales del cierre (Ej: avanzar paso)
 * @param {number} closureId 
 * @param {{ currentStep?: number }} data 
 */
export async function updatePayrollClosure(closureId, data) {
  return httpClient.patch(`/hr/areas/payroll/closures/${closureId}`, data);
}

/**
 * Confirmar cierre, pasar a READY, y generar recibos/reportes
 * @param {number} closureId 
 */
export async function confirmPayrollClosure(closureId) {
  return httpClient.post(`/hr/areas/payroll/closures/${closureId}/confirm`);
}

/**
 * Regenerar el reporte contable de un cierre completado.
 * @param {number} closureId
 */
export async function regenerateAccountantReport(closureId) {
  return httpClient.post(`/hr/areas/payroll/closures/${closureId}/accountant-report/regenerate`);
}

/**
 * Regenerar el recibo de un empleado de un cierre completado.
 * @param {number} closureId
 * @param {number} employeeId
 */
export async function regenerateEmployeeReceipt(closureId, employeeId) {
  return httpClient.post(
    `/hr/areas/payroll/closures/${closureId}/employees/${employeeId}/receipt/regenerate`,
  );
}

/**
 * Descarga un archivo PDF (Reporte o Recibo) usando el token de sesión
 * @param {string} fileName 
 */
export async function downloadPayrollFile(fileName) {
  return httpClient.get(`/hr/areas/payroll/downloads/${fileName}`, {
    responseType: 'blob', 
  });
}

/**
 * Obtener empleados faltantes (que no fueron liquidados)
 */
export async function listMissingEmployees() {
  return httpClient.get('/hr/areas/payroll/closures/missing/employees');
}

/**
 * Eliminar a un empleado de la liquidación (útil para descartar los que quedan en $0)
 * @param {number} closureId 
 * @param {number} employeeId 
 */
export async function removeClosureEmployee(closureId, employeeId) {
  return httpClient.delete(`/hr/areas/payroll/closures/${closureId}/employees/${employeeId}`);
}

/**
 * Eliminar un cierre completo que esté en estado DRAFT
 * @param {number} closureId 
 */
export async function deleteDraftClosure(closureId) {
  return httpClient.delete(`/hr/areas/payroll/closures/${closureId}`);
}
